import React, { useState } from 'react';
import {
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
  Dialog,
  DialogContent,
  useMediaQuery,
} from '@mui/material';
import { Delete, Edit, MoreVert, PushPin, PushPinOutlined, Close, ArrowBack, ArrowForward } from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  useDeleteAnnouncementMutation,
  usePinAnnouncementMutation,
  useUnpinAnnouncementMutation,
} from '../announcementApiSlice';
import UserAvatar from '../../../common/UserAvatar';
import ChatBubble from './ChatBubble';
import LinkPreview from './LinkPreview';
import { formatTime } from '../../../utils/dateUtils';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmpCode } from '../../Auth/authSlice';
import AttachAvatar from '../../../common/AttachAvatar';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';

const AnnouncementItem = ({ announcement, user, setEditingAnnouncement }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const userEmpCode = useSelector(selectCurrentUserEmpCode);
  const [deleteAnnouncement] = useDeleteAnnouncementMutation();
  const [pinAnnouncement] = usePinAnnouncementMutation();
  const [unpinAnnouncement] = useUnpinAnnouncementMutation();
  const ability = useAbility(AbilityContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditAnnouncement = () => {
    setEditingAnnouncement({
      announcementId: announcement._id,
      announcementText: announcement.announcementText,
      severity: announcement.severity,
      category: announcement.category,
      attachments: announcement.attachments,
      country: announcement.country,
      regions: announcement.regions,
    });
    handleClose();
  };

  const handleDeleteAnnouncement = async () => {
    // await toast.promise(deleteAnnouncement(announcement._id), {
    //   loading: 'Deleting announcement...',
    //   success: 'Announcement deleted successfully!',
    //   error: err => err.data?.message || 'Error deleting announcement. Please try again.',
    // });
    await deleteAnnouncement(announcement._id);
    handleClose();
  };

  const handlePinAnnouncement = async () => {
    // await toast.promise(pinAnnouncement(announcement._id), {
    //   loading: 'Pinning announcement...',
    //   success: 'Announcement pinned successfully!',
    //   error: err => err.data?.message || 'Error pinning announcement. Please try again.',
    // });
    await pinAnnouncement(announcement._id);
    handleClose();
  };

  const handleUnpinAnnouncement = async () => {
    // await toast.promise(unpinAnnouncement(announcement._id), {
    //   loading: 'Unpinning announcement...',
    //   success: 'Announcement unpinned successfully!',
    //   error: (err) => err.data?.message || 'Error unpinning announcement. Please try again.',
    // });
    await unpinAnnouncement(announcement._id);
    handleClose();
  };

  const handleAttachmentClick = (index) => {
    setSelectedAttachmentIndex(index);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAttachmentIndex(null);
  };
  const handlePreviousAttachment = () => {
    setSelectedAttachmentIndex(
      (prevIndex) => (prevIndex - 1 + announcement.attachments.length) % announcement.attachments.length
    );
  };

  const handleNextAttachment = () => {
    setSelectedAttachmentIndex((prevIndex) => (prevIndex + 1) % announcement.attachments.length);
  };

  return (
    <>
      <ListItem
        alignItems='flex-start'
        sx={{ flexDirection: announcement.authorEmpCode === userEmpCode ? 'row-reverse' : 'row' }}
      >
        {announcement.authorEmpCode !== userEmpCode && (
          <UserAvatar
            showStatus={false}
            empCode={announcement.authorEmpCode}
            src={announcement.authorUser?.photo}
            sx={{ height: '30px', width: '30px', marginRight: 1, marginTop: '5px', ml: '-10px' }}
          />
        )}
        <ListItemText
          primary={
            <Box
              sx={{
                display: 'flex',
                justifyContent: announcement.authorEmpCode === userEmpCode ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end',
              }}
            >
              <Typography variant='caption' color='#555' sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                {announcement.authorEmpCode === userEmpCode ? (
                  formatTime(announcement?.datePosted)
                ) : (
                  <>
                    {announcement.authorUser?.username}, {formatTime(announcement?.datePosted)}{' '}
                  </>
                )}
              </Typography>
              {(announcement.authorEmpCode === userEmpCode || ability.can('read', 'Admin')) && (
                <IconButton
                  aria-label='more'
                  aria-controls='short-menu'
                  aria-haspopup='true'
                  onClick={handleClick}
                  size='small'
                  sx={{ ml: 0 }}
                >
                  <MoreVert sx={{ fontSize: '14px' }} />
                </IconButton>
              )}
            </Box>
          }
          secondary={
            <>
              {announcement.attachments && announcement.attachments.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    mb: 1,
                    justifyContent: announcement.authorEmpCode === userEmpCode ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                  }}
                >
                  {announcement.attachments.map((attachment, index) => (
                    <Box key={index} onClick={() => handleAttachmentClick(index)}>
                      <Tooltip title={attachment.split('/').pop()} arrow>
                        <AttachAvatar
                          src={attachment}
                          controls={false}
                          autoPlay={false}
                          muted={true}
                          variant='rounded'
                          className='attachment-avatar'
                          sx={{
                            width: 100,
                            height: 100,
                            margin: 0.5,
                            objectFit: 'cover',
                            cursor: 'default',
                            aspectRatio: '1 / 1',
                          }}
                        />
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
              <LinkPreview isOwnMessage={announcement.authorEmpCode === userEmpCode} announcement={announcement} />
              <ChatBubble isOwnMessage={announcement.authorEmpCode === userEmpCode} announcement={announcement} />
            </>
          }
        />
        <Menu id='short-menu' anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
          {announcement.category == 'all' ? (
            announcement.isPinned ? (
              <MenuItem onClick={handleUnpinAnnouncement}>
                <PushPinOutlined sx={{ marginRight: 1, fontSize: '14px' }} /> Unpin
              </MenuItem>
            ) : (
              <MenuItem onClick={handlePinAnnouncement}>
                <PushPin sx={{ marginRight: 1, fontSize: '14px' }} /> Pin
              </MenuItem>
            )
          ) : null}
          <MenuItem onClick={handleEditAnnouncement}>
            <Edit sx={{ marginRight: 1, fontSize: '14px' }} /> Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteAnnouncement}>
            <Delete sx={{ marginRight: 1, fontSize: '14px' }} /> Delete
          </MenuItem>
        </Menu>
      </ListItem>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth='lg'
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            height: isMobile ? '50vh' : '75vh',
            width: '100%',
            overflowX: isMobile ? 'auto' : 'hidden',
          }}
        >
          <IconButton
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              top: isMobile ? 8 : 16,
              right: isMobile ? 8 : 16,
              background: 'lightgrey',
              zIndex: 2,
            }}
          >
            <Close fontSize={isMobile ? 'small' : 'medium'} sx={{ color: 'black', m: '-5px' }} />
          </IconButton>

          {selectedAttachmentIndex !== null && (
            <>
              <AttachAvatar
                src={announcement.attachments[selectedAttachmentIndex]}
                controls
                autoPlay
                variant='rounded'
                sx={{
                  width: 'auto',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
              />

              {announcement.attachments.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousAttachment}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: isMobile ? 8 : 16,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      transform: 'translateY(-50%)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ArrowBack fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>

                  <IconButton
                    onClick={handleNextAttachment}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: isMobile ? 8 : 16,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      transform: 'translateY(-50%)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ArrowForward fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementItem;
